import React, { useEffect, useRef, useState } from 'react';
import { useApiClient } from '../api/client';
import { useI18n } from '../i18n/I18nProvider';
import { GroupOptimizerPlanResult, OptimizerResult, OptimizerScenario } from './GroupOptimizerPlanResult';

type Job = {
  id: string; start: string; status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED'; createdAt: string;
  maxSeconds?: number; progress?: { stage: string; elapsed_seconds: number; max_seconds: number; candidate_count?: number };
  current?: boolean; scenario?: OptimizerScenario; result?: OptimizerResult; error?: string; warnings: string[];
};
const running = (job: Job | null) => job?.status === 'QUEUED' || job?.status === 'RUNNING';
type History = Pick<Job, 'id' | 'start' | 'status' | 'createdAt' | 'maxSeconds'>;
const historyJob = ({id,start,status,createdAt,maxSeconds}: History): Job => ({id,start,status,createdAt,maxSeconds,warnings:[]});

export function GroupOptimizerPlanPanel({ base, start, canCalculate, onBusyChange }: {
  base: string; start: string; canCalculate: boolean; onBusyChange: (busy: boolean) => void;
}) {
  const { request } = useApiClient(); const { t } = useI18n();
  const [job, setJob] = useState<Job | null>(null);
  const [history, setHistory] = useState<History[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [maxSeconds, setMaxSeconds] = useState(60);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const busy = starting || running(job);
  const jobId = job?.id; const jobStatus = job?.status;
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  useEffect(() => {
    const current = ++generation.current;
    setJob(null); setHistory([]); setError(''); setLoading(true); setStarting(false);
    request<History[]>(`${base}/history`, { cache: 'no-store' })
      .then(jobs => { if (generation.current === current) {
        setHistory(jobs);
        const selected = jobs.find(item => item.start === start);
        setJob(selected ? historyJob(selected) : null);
      } })
      .catch(err => { if (generation.current === current) setError(err.message); })
      .finally(() => { if (generation.current === current) setLoading(false); });
    return () => { generation.current = current + 1; };
  }, [base, start, request]);
  useEffect(() => {
    if (!jobId || !['QUEUED','RUNNING','COMPLETED','FAILED'].includes(jobStatus ?? '')) return;
    let active = true; let timeout: number;
    const id = jobId;
    async function poll() {
      try {
        const value = await request<Job>(`${base}/${encodeURIComponent(id)}`, { cache: 'no-store' });
        if (!active) return;
        setJob(value); setError('');
        setHistory(old => old.map(item => item.id === value.id ? value : item));
        if (running(value) || value.result) timeout = window.setTimeout(poll, running(value)?1000:15000);
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : t('optimizer.planFailed'));
          setJob(old=>old?.id===id?{...old,current:false}:old);
          setHistory(old=>old.map(item=>item.id===id?{...item,current:false}:item));
          timeout = window.setTimeout(poll, 3000);
        }
      }
    }
    void poll();
    return () => { active = false; window.clearTimeout(timeout); };
  }, [base, jobId, jobStatus, request, t]);
  async function calculate() {
    if (busy || loading || !canCalculate) return;
    const current = generation.current;
    setStarting(true); setError('');
    try {
      const value = await request<Job>(base, { method: 'POST', body: JSON.stringify({ start, maxSeconds }), cache: 'no-store' });
      if (generation.current === current) { setJob(value); setHistory(old => [value, ...old.filter(item => item.id !== value.id)]); }
    } catch (err) {
      if (generation.current === current) setError(err instanceof Error ? err.message : t('optimizer.planFailed'));
    } finally { if (generation.current === current) setStarting(false); }
  }
  const phase = job?.progress?.stage || (job?.scenario ? 'starting' : 'routing');
  const knownPhase = ['routing','starting','candidates','coverage','solo','baseline','optimal','fair','verifying','completed'].includes(phase) ? phase : 'starting';
  const elapsed = job?.progress?.elapsed_seconds;
  const allotted = job?.maxSeconds ?? job?.progress?.max_seconds;
  return <section className="mt-5 border-t border-slate-200 pt-5" aria-label={t('optimizer.planSection')}>
    <label className="block text-sm font-medium" htmlFor="optimizer-search-duration">{t('optimizer.searchDuration')}</label>
    <select id="optimizer-search-duration" className="form-control mt-2 w-full" value={running(job) ? allotted ?? maxSeconds : maxSeconds} disabled={busy || loading}
      onChange={e => setMaxSeconds(Number(e.target.value))}>
      {[30,60,120].map(seconds => <option key={seconds} value={seconds}>{t(`optimizer.duration.${seconds}`)}</option>)}
    </select>
    <p className="mt-2 mb-4 text-xs leading-relaxed text-slate-500">{t('optimizer.durationNote')}</p>
    <button className="btn btn-primary" disabled={!canCalculate || busy || loading} onClick={calculate}>{t('optimizer.calculate')}</button>
    {loading && <p role="status" className="mt-3 text-sm text-slate-500">{t('optimizer.historyLoading')}</p>}
    {starting && <p role="status" className="mt-3 text-sm">{t('optimizer.starting')}</p>}
    {job && running(job) && <div role="status" aria-live="polite" className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm">
      <p className="font-semibold">{t(job.status === 'QUEUED' ? 'optimizer.queued' : `optimizer.phase.${knownPhase}`)}</p>
      <p className="mt-2 text-slate-600">{t(job.status === 'QUEUED' ? 'optimizer.queueNote' : `optimizer.phaseNote.${knownPhase}`)}</p>
      {job.status === 'RUNNING' && elapsed != null && allotted != null && <>
        <p className="mt-3 tabular-nums font-medium">{t('optimizer.searchElapsed',{seconds:Math.round(elapsed),budget:allotted})}</p>
        {elapsed >= allotted && <p className="mt-1 text-xs text-slate-600">{t('optimizer.finishingSearch')}</p>}
      </>}
      {job.progress?.candidate_count != null && <p className="mt-1 text-xs text-slate-600">{t('optimizer.candidatesFound',{count:job.progress.candidate_count})}</p>}
      <p className="mt-3 text-xs text-slate-600">{t('optimizer.backgroundNote')}</p>
    </div>}
    {job?.status === 'COMPLETED' && job.result?.elapsed_seconds != null && <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
      <p className="font-medium tabular-nums">{t(allotted == null?'optimizer.searchFinishedLegacy':'optimizer.searchFinished',
        {seconds:Math.round(job.result.elapsed_seconds),budget:allotted ?? 30})}</p>
      {job.result.search && <p className="mt-1 text-xs">{t('optimizer.candidatesFound',{count:job.result.search.count})}. {t(job.result.search.truncated?'optimizer.searchTruncated':'optimizer.boundedSearch')}</p>}
    </div>}
    <details className="mt-4 text-sm text-slate-600"><summary className="cursor-pointer">{t('optimizer.processTitle')}</summary>
      <ol className="mt-2 list-decimal space-y-2 pl-5">{['routing','candidates','coverage','solo','baseline','optimal','fair','verifying'].map(step =>
        <li key={step}><span className="font-medium">{t(`optimizer.phase.${step}`)}</span><span className="block text-xs mt-1">{t(`optimizer.phaseNote.${step}`)}</span></li>)}</ol>
    </details>
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
    {job?.status === 'FAILED' && <p role="alert" className="mt-3 text-sm text-red-700">{job.error || t('optimizer.planFailed')}</p>}
    {history.length > 0 && <label className="block mt-4 text-sm">{t('optimizer.history')}
      <select className="form-control mt-1 w-full" value={job?.id || ''} disabled={busy} onChange={e => {
        const selected = history.find(item => item.id === e.target.value);
        setError(''); setJob(selected ? historyJob(selected) : null);
      }}>
        <option value="">{t('optimizer.history.choose')}</option>
        {history.map(item => <option key={item.id} value={item.id}>{new Date(item.createdAt).toLocaleString()} · {t('optimizer.history.week',{date:item.start})} · {t(`optimizer.status.${item.status}`)}</option>)}
      </select>
    </label>}
    {job?.warnings?.map((warning, i) => <p key={i} className="mt-3 text-sm text-amber-800">{warning}</p>)}
    {job?.result && job.scenario && <GroupOptimizerPlanResult key={job.id} scenario={job.scenario} result={job.result} createdAt={job.createdAt} current={job.current} />}
    <p className="mt-3 text-xs text-slate-500">{t('optimizer.draftNote')}</p>
  </section>;
}
